/**
 * «Контроль → Инкассация»: каждая инкассация наличной кассы CRM и внесение
 * наличных на карту её города, которое её подтверждает. Сверху — итог по
 * городам, ниже — инкассации (по умолчанию те, что не прошли) и внесения на
 * карты без инкассации.
 *
 * Спека: `docs/specs/2026-10-02-control-collection-design.md`.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { DateRangePicker, withPeriod } from "@/components/DateRangePicker";
import { NoAccess } from "@/components/NoAccess";
import { CONTROL, useCan } from "@/lib/access";
import {
  type CollectionCity,
  type CollectionReport,
  type CollectionStatus,
  loadCollection,
  STATUS_HINT,
  STATUS_LABEL,
} from "@/lib/control";
import { money } from "@/lib/pnl";

const TH = "whitespace-nowrap border-b border-border px-2.5 py-2 text-right align-bottom";
const CELL = "whitespace-nowrap border-b border-border px-2.5 py-1 text-right tabular-nums";
const NAME = "border-b border-border px-3 py-1 text-left";
const EDGE = "border-l border-border";

const TONE: Record<CollectionStatus, string> = {
  found: "border-ok/30 bg-ok-bg text-ok",
  missing: "border-err/30 bg-err-bg text-err",
  no_statement: "border-warn/30 bg-warn-bg text-warn",
  no_account: "border-border bg-bg-subtle text-fg-muted",
};

/** Отбор списка. «Не прошли» — всё, кроме найденного. */
type Show = "problem" | CollectionStatus | "all";
const SHOWS: { code: Show; label: string }[] = [
  { code: "problem", label: "не прошли" },
  { code: "missing", label: "не найдено" },
  { code: "no_statement", label: "нет выписки" },
  { code: "found", label: "найдено" },
  { code: "all", label: "все" },
];

function day(value: string | null): string {
  return value ? value.split("-").reverse().join(".") : "—";
}

function Badge({ status }: { status: CollectionStatus }) {
  return (
    <span title={STATUS_HINT[status]} className={`rounded border px-1.5 py-px text-[11px] ${TONE[status]}`}>
      {STATUS_LABEL[status]}
    </span>
  );
}

/** «7 · 1 327 200»; ноль — прочерк. */
function Pair({ count, amount, tone = "" }: { count: number; amount: string; tone?: string }) {
  if (!count) return <span className="text-fg-faint">—</span>;
  return (
    <span className={tone}>
      <span className="text-fg-muted">{count} · </span>
      {money(amount, true)}
    </span>
  );
}

function Cities({
  report,
  city,
  onCity,
}: {
  report: CollectionReport;
  city: string;
  onCity: (name: string) => void;
}) {
  const row = (c: CollectionCity | (CollectionReport["total"] & { name: string; accounts: [] }), total = false) => (
    <tr
      key={c.name}
      className={total ? "bg-bg-subtle font-medium" : `cursor-pointer hover:bg-bg-hover ${city === c.name ? "bg-bg-hover" : ""}`}
      onClick={total ? undefined : () => onCity(city === c.name ? "" : c.name)}
      title={total ? undefined : "Показать инкассации города"}
    >
      <td className={`${NAME} whitespace-nowrap ${total ? "" : "font-medium"}`}>{c.name}</td>
      <td className={`${NAME} text-[11.5px] text-fg-muted`}>
        {c.accounts.map((a) => (
          <div key={a.id} className="whitespace-nowrap">
            {a.account} <span className="text-fg-faint">{a.bank_name}</span> ·{" "}
            {a.covered_to ? `выписки по ${day(a.covered_to)}` : <span className="text-warn">выписок нет</span>}
          </div>
        ))}
      </td>
      <td className={`${CELL} ${EDGE}`}>
        <Pair count={c.count} amount={c.amount} />
      </td>
      <td className={CELL}>
        <Pair count={c.found_count} amount={c.found_amount} tone="text-ok" />
      </td>
      <td className={CELL}>
        <Pair count={c.missing_count} amount={c.missing_amount} tone="text-err" />
      </td>
      <td className={CELL}>
        <Pair count={c.no_statement_count + c.no_account_count} amount={String(Number(c.no_statement_amount) + Number(c.no_account_amount))} tone="text-warn" />
      </td>
    </tr>
  );
  return (
    <section data-annot="итог по городам" className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
      <table className="w-full border-collapse text-[12px]">
        <thead>
          <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
            <th className={`${NAME} align-bottom`}>Город</th>
            <th className={`${NAME} align-bottom`}>куда вносят</th>
            <th className={`${TH} ${EDGE}`}>инкассаций</th>
            <th className={TH} title={STATUS_HINT.found}>
              найдено
            </th>
            <th className={TH} title={STATUS_HINT.missing}>
              не найдено
            </th>
            <th className={TH} title={STATUS_HINT.no_statement}>
              нет выписки
            </th>
          </tr>
        </thead>
        <tbody>
          {report.cities.map((c) => row(c))}
          {row({ ...report.total, name: "Итого", accounts: [] }, true)}
        </tbody>
      </table>
    </section>
  );
}

export function CollectionPage() {
  return useCan(CONTROL) ? <CollectionScreen /> : <NoAccess what="контролю" />;
}

function CollectionScreen() {
  const [params, setParams] = useSearchParams();
  const [report, setReport] = useState<CollectionReport | null>(null);
  const [failure, setFailure] = useState("");

  // Без периода — этот год: сверяем год с тем же годом.
  const year = new Date().getFullYear();
  const from = params.get("date_from") ?? `${year}-01-01`;
  const to = params.get("date_to") ?? `${year}-12-31`;
  const show = (params.get("show") as Show | null) ?? "problem";
  const city = params.get("city") ?? "";

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  useEffect(() => {
    if (!from || !to) return;
    setFailure("");
    setReport(null);
    loadCollection({ date_from: from, date_to: to })
      .then(setReport)
      .catch((error: Error) => setFailure(error.message));
  }, [from, to]);

  const items = (report?.collections ?? []).filter(
    (c) =>
      (!city || c.city === city) &&
      (show === "all" || (show === "problem" ? c.status !== "found" : c.status === show)),
  );

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4 [&>*]:flex-none">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[12.5px] text-fg-muted">
        <DateRangePicker label="период" from={from} to={to} onChange={(f, t) => setParams(withPeriod(params, f, t))} />
        <span>
          Инкассация — выплата наличной кассы CRM со статьёй «Перемещение / Инкасация» или словом «инкассация» в
          комментарии. Пара — внесение наличных той же суммы на карту города, от двух дней раньше до пяти позже
          (или несколько внесений одного дня в сумме).
        </span>
      </div>
      {failure ? (
        <div role="alert" className="rounded-[var(--radius-ui)] border border-err/30 bg-err-bg px-3 py-2 text-[12.5px] text-err">
          {failure}
        </div>
      ) : null}
      {report === null ? (
        failure ? null : <div className="text-[12.5px] text-fg-muted">…</div>
      ) : report.collections.length === 0 ? (
        <div className="text-[12.5px] text-fg-muted">За период инкассаций в CRM нет.</div>
      ) : (
        <>
          <Cities report={report} city={city} onCity={(name) => setParam("city", name)} />

          <div className="flex flex-wrap items-center gap-1.5 text-[12px]">
            {SHOWS.map((s) => (
              <button
                key={s.code}
                type="button"
                onClick={() => setParam("show", s.code === "problem" ? "" : s.code)}
                className={`rounded-[var(--radius-ui)] border px-2 py-1 ${
                  show === s.code ? "border-fg bg-bg-hover text-fg" : "border-border text-fg-muted hover:bg-bg-hover"
                }`}
              >
                {s.label}
              </button>
            ))}
            {city ? (
              <button
                type="button"
                onClick={() => setParam("city", "")}
                className="rounded-[var(--radius-ui)] border border-border px-2 py-1 text-fg-muted hover:bg-bg-hover"
                title="Снять отбор города"
              >
                {city} ×
              </button>
            ) : null}
            <span className="ml-1 text-fg-muted">{items.length} шт.</span>
          </div>

          <section data-annot="инкассации" className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
            <table className="w-full border-collapse text-[12px]">
              <thead>
                <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
                  <th className={`${NAME} align-bottom`}>дата</th>
                  <th className={`${NAME} align-bottom`}>город</th>
                  <th className={TH}>сумма</th>
                  <th className={`${NAME} align-bottom`}>комментарий CRM</th>
                  <th className={`${NAME} align-bottom`}>статус</th>
                  <th className={`${NAME} ${EDGE} align-bottom`}>внесение на карту</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id} className="align-top">
                    <td className={`${NAME} whitespace-nowrap tabular-nums`}>{day(c.date)}</td>
                    <td className={`${NAME} whitespace-nowrap`}>{c.city}</td>
                    <td className={CELL}>{money(c.amount)}</td>
                    <td className={`${NAME} text-fg-muted`} title={`выплата CRM №${c.crm_id}, ${c.kassa}`}>
                      {c.comment || "—"}
                    </td>
                    <td className={NAME}>
                      <Badge status={c.status} />
                    </td>
                    <td className={`${NAME} ${EDGE} text-fg-muted`}>
                      {c.deposits.length === 0
                        ? "—"
                        : c.deposits.map((d) => (
                            <div key={d.id} className="whitespace-nowrap tabular-nums" title={d.purpose}>
                              {day(d.date)} · {money(d.amount)} · {d.account}{" "}
                              <span className="text-fg-faint">{d.bank_name}</span>
                            </div>
                          ))}
                    </td>
                  </tr>
                ))}
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className={`${NAME} py-3 text-fg-muted`}>
                      Под отбор ничего не попало.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </section>

          <section data-annot="внесения без инкассации" className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
            <div className="border-b border-border bg-bg-subtle px-3 py-2 text-[12px] font-medium">
              Внесения на карты без инкассации в CRM
              <span className="ml-2 font-normal text-fg-muted">
                {report.orphans.length ? `${report.orphans.length} шт. · ${money(report.orphans_amount)}` : "нет"}
              </span>
            </div>
            {report.orphans.length ? (
              <table className="w-full border-collapse text-[12px]">
                <tbody>
                  {report.orphans.map((d) => (
                    <tr key={d.id}>
                      <td className={`${NAME} w-[90px] whitespace-nowrap tabular-nums`}>{day(d.date)}</td>
                      <td className={`${CELL} w-[120px]`}>{money(d.amount)}</td>
                      <td className={`${NAME} whitespace-nowrap`}>
                        {d.account} <span className="text-fg-faint">{d.bank_name}</span>
                      </td>
                      <td className={`${NAME} text-fg-muted`}>{d.purpose}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </section>
        </>
      )}
    </main>
  );
}
